// How a saved Test's lab report was checked (TGCLOUD-1868, plan B5, D6): each group the person
// saving confirmed, who they were, their role, when, and what the save found it to be, in words.
// Read-only, at the head of the Test pane while a saved Test's report is open.
//
// The groups are the record's (GET /test-results/:id/anchors, provenance.groups): codes and paths,
// never a value. A variant's group is named by the Test's variant with that findingId, as a
// clinician names it; a role by its name in Settings (CONSTANTS.userTypeOptionsMap), never its code;
// a time by the reader's own clock (confirmedAt is an instant: toLocalDateFromInstant).
import React from 'react';
import moment from 'moment';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import { CONSTANTS } from 'src/components/Constants';
import { variantLabel } from 'src/components/pedigree-chart/pedigree-sidebar/sections/test-results/applyProposal';
import { english } from 'src/i18n/language';
import {
  GROUP_KIND_TEXT,
  NO_CONFIRMATIONS,
  outcomeText,
  typedMatchCountText
} from './labReportVerificationVocabulary';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const PANEL_SX = {
  p: 2,
  mb: 2,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1
};

const capitalised = (text) =>
  text ? `${text[0].toUpperCase()}${text.slice(1)}` : text;

const roleText = (role) =>
  (typeof role === 'string' &&
    Object.prototype.hasOwnProperty.call(CONSTANTS.userTypeOptionsMap, role) &&
    CONSTANTS.userTypeOptionsMap[role]) ||
  null;

/**
 * A group's name: "Gene list", "Variant MLH1 c.1852_1854del", "Laboratory classification of …", in
 * the caller's `t` (English by default).
 */
export function groupName(group, variants = [], t = english) {
  const kind = t(GROUP_KIND_TEXT[group?.kind] || 'Part of the report');
  if (
    group?.kind !== 'variant-identity' &&
    group?.kind !== 'variant-classification'
  ) {
    return kind;
  }
  const variant = (Array.isArray(variants) ? variants : []).find(
    (v) => v && group.findingId && v.findingId === group.findingId
  );
  const named = variant ? variantLabel(variant) : '';
  if (!named) return t('{{kind}}, no longer on this Test', { kind });
  return group.kind === 'variant-identity'
    ? t('Variant {{named}}', { named })
    : t('{{kind}} of {{named}}', { kind, named });
}

/** What was typed into a group, against the reading: '' when nothing was. */
export function typedSummary(typed, t = english) {
  const list = Array.isArray(typed) ? typed : [];
  if (!list.length) return '';
  const counts = new Map();
  for (const t of list) {
    const key = String(
      t && (t.matchedReading === true || t.matchedReading === false)
        ? t.matchedReading
        : null
    );
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const parts = ['true', 'false', 'null']
    .filter((k) => counts.has(k))
    .map((k) => typedMatchCountText(k, counts.get(k), t));
  return t('Entered by hand: {{parts}}.', { parts: parts.join(', ') });
}

// "12/03/2026 at 14:05": an instant, by the reader's own clock; '' when there is none.
const whenText = (at, fmtDate, t) => {
  const instant = toLocalDateFromInstant(at);
  if (!instant) return '';
  return t('{{date}} at {{time}}', {
    date: fmtDate(instant),
    time: moment(instant).format('HH:mm')
  });
};

/**
 * @param {object} props
 * @param {'loading'|'ready'|'failed'} props.status   the anchors' read
 * @param {object[]} props.groups      provenance.groups, as GET /:id/anchors answers them
 * @param {object[]} props.variants    the Test's variants, to name a variant's groups
 * @param {{ acknowledgedAt }} [props.unverifiable]  provenance.unverifiable, when recorded
 */
export default function SavedReportChecks({
  status,
  groups = [],
  variants = [],
  unverifiable = null
}) {
  const t = useAutoTranslation();
  const fmtDate = useDateFormatter();

  const list = Array.isArray(groups) ? groups : [];
  return (
    <Box data-testid="saved-report-checks" sx={PANEL_SX}>
      <Typography
        component="h3"
        variant="subtitle2"
        sx={{ fontWeight: 700, mb: 1 }}
      >
        {t('How this report was checked')}
      </Typography>
      {status === 'loading' && (
        <Box
          role="status"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <CircularProgress size={16} />
          <Typography variant="body2">
            {t('Reading where each value is printed…')}
          </Typography>
        </Box>
      )}
      {status === 'failed' && (
        <Alert severity="warning" data-testid="saved-report-checks-failed">
          {t(
            'Where each value is printed, and how the report was checked, could not be loaded. The report is shown as it is.'
          )}
        </Alert>
      )}
      {status === 'ready' && !list.length && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t(NO_CONFIRMATIONS)}
        </Typography>
      )}
      {status === 'ready' &&
        list.map((group, i) => {
          const role = roleText(group.confirmedByRole);
          const typed = typedSummary(group.typed, t);
          const when = whenText(group.confirmedAt, fmtDate, t);
          return (
            <Box
              key={group.key || i}
              data-testid="saved-report-check"
              data-group={group.key || ''}
              sx={{ py: 0.75, borderTop: i ? 1 : 0, borderColor: 'divider' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {groupName(group, variants, t)}
              </Typography>
              <Typography variant="body2">
                {`${capitalised(outcomeText(group.outcome, t))}.`}
                {typed ? ` ${typed}` : ''}
              </Typography>
              <Typography
                variant="caption"
                component="div"
                sx={{ color: 'text.secondary' }}
              >
                {/* The name is the person's, as recorded; the role is the app's own word for it. */}
                {[group.confirmedByName || t('A clinician'), role && t(role)]
                  .filter(Boolean)
                  .join(', ')}
                {when ? `, ${t('on {{when}}', { when })}` : null}
              </Typography>
            </Box>
          );
        })}
      {unverifiable?.acknowledgedAt && (
        <Typography
          variant="body2"
          component="div"
          data-testid="saved-report-unverifiable"
          sx={{ mt: 1, color: 'warning.dark' }}
        >
          {t(
            'The report could not be shown when it was applied. The person who saved it said they had checked these values against it another way, on {{when}}.',
            { when: whenText(unverifiable.acknowledgedAt, fmtDate, t) }
          )}
        </Typography>
      )}
    </Box>
  );
}
